import React, { useState, useEffect, useContext } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { loadCheckList, updateCheckList } from '../actionCreators';
import { selectCheckList } from '../selectors';
import * as editableDocument from 'utils/model/editableDocument';
import { collection } from 'utils/immutable';
import { when, valueChanged } from 'utils/fn-combinators';
import CheckListComponent from './CheckListComponent';
import { RightsContext } from 'providers/RightsContext';

import { loadChecklists } from 'actions/actionCreators/dictionariesActions';
import { getFolderOfferData } from '../../../../folder/wizard/selectors';
import { getOfferFolderData } from '../../../../../components/offer/edit/selectors';
import { selectChecklistsLoaded, selectChecklists } from '../selectors';
import { selectCurrentUserName, selectUserId } from '../../../../../selectors/userInfoSelectors';

import debounce from 'lodash.debounce';

import { isMandatory, isUnique } from 'utils/simple-validations';
import flow from 'lodash.flow';

const CheckListContainer = ({
  documentId,
  siteId,
  checkList,
  checkListLoaded,
  userName,
  userId,
  checklistsLoaded,
  checklists,
  isFromFolder
}) => {
  const getValidationRules = collection => {
    return {
      checklistId: [
        isUnique(
          x => x?.document?.checklistId,
          document => collection.filter(x => x.document.id !== document.id)
        ),
        isMandatory()
      ]
    };
  };

  const getBusinessRules = () => ({
    fulfilledOn: when(
      valueChanged(document => document.fulfilled),
      document => (document.fulfilled ? new Date().toLocaleString() : null)
    ),

    fulfillerId: when(
      valueChanged(document => document.fulfilled),
      document => (document.fulfilled ? userId : null)
    ),

    fulfillerName: when(
      valueChanged(document => document.fulfilled),
      document => (document.fulfilled ? userName : null)
    )
  });

  const emptyRow = {
    documentId: null,
    siteId: null,
    addedOn: null,
    fulfillerId: null,
    fulfillerName: null,
    userId: null,
    userName: null,
    checklistId: null,
    condition: '',
    fulfilled: false,
    fulfilledOn: null
  };

  const dispatch = useDispatch();

  const [localCheckList, setLocalCheckList] = useState(checkList);
  const [localFilteredCheckList, setLocalFilteredCheckList] = useState(null);
  const [saving, setSaving] = useState(false);
  const [isListDirty, setListDirty] = useState(false);
  const [itemsToDelete, setItemsToDelete] = useState(0);
  const [newItemId, setNewItemId] = useState(-1);

  const defaultConfig = { page: 1, pageSize: 5, totalCount: 0, filter: '' };
  const [paginationAndFilterObj, setPaginationAndFilterObj] = useState(defaultConfig);
  const rowsPerPageOptions = [5, 10, 20];
  const DEBOUNCE_DELAY = 50;

  const offerData = useSelector(state => getFolderOfferData(state), shallowEqual);
  const folderData = useSelector(state => getOfferFolderData(state), shallowEqual);

  const { readOnlyFull, documentChecklistWrite, addDeleteApprovalConditions } = useContext(RightsContext);

  useEffect(() => {
    if (checkList) {
      setPaginationAndFilterObj(prevState => ({
        ...prevState,
        totalCount: checkList.length
      }));
    }
  }, [checkList]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!checkListLoaded) dispatch(loadCheckList(documentId, siteId));
  }, [checkListLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!checklistsLoaded) dispatch(loadChecklists());
  }, [checklistsLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (checkListLoaded) {
      const doc = checkList.map(item => editableDocument.create(item));
      setLocalCheckList(doc);
      setLocalFilteredCheckList(
        getItemsPerPage(doc, paginationAndFilterObj.pageSize, paginationAndFilterObj.page, paginationAndFilterObj.filter)
      );
    }
  }, [documentId, siteId, checkListLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  const changeItemProperty = index => prop => value => {
    const updatedList = collection.update(
      index + paginationAndFilterObj.pageSize * (paginationAndFilterObj.page - 1),
      flow(editableDocument.update(prop, value, getBusinessRules())),
      localCheckList
    );
    const validatedList = validateCollection(updatedList);

    const updatedFilteredList = collection.update(
      index,
      flow(editableDocument.update(prop, value, getBusinessRules())),
      localFilteredCheckList
    );
    const validatedFilteredList = validateCollection(updatedFilteredList);

    setListDirty(getItemsDirtyStatus(updatedList) || false);

    setLocalCheckList(validatedList);
    setLocalFilteredCheckList(validatedFilteredList);
  };

  const getItemsDirtyStatus = collection => {
    if (!collection) return false;
    return collection.some(item => item.dirtyInfo.isDirty);
  };

  const getItemsValidity = collection => {
    if (!collection) return false;
    return collection.every(item => item.validationInfo?.isValid);
  };

  const addItem = () => {
    const newRow = editableDocument.create({
      ...emptyRow,
      documentId: isFromFolder ? offerData.offerId : documentId,
      siteId: isFromFolder ? offerData.offerSiteId : siteId,
      addedOn: new Date().toLocaleString(),
      userId: userId,
      userName: userName,
      id: newItemId
    });

    setNewItemId(newItemId - 1);

    const newChecklist = collection.insert(localCheckList.length, newRow, localCheckList);
    setLocalCheckList(newChecklist);
    setListDirty(true);

    setLocalFilteredCheckList(
      getItemsPerPage(newChecklist, paginationAndFilterObj.pageSize, paginationAndFilterObj.page, paginationAndFilterObj.filter)
    );

    if (localFilteredCheckList && localFilteredCheckList.length + 1 > paginationAndFilterObj.pageSize)
      onChangePage(paginationAndFilterObj.page, newChecklist.length, newChecklist);
  };

  const deleteItems = () => {
    const itemsToDelete = localCheckList.filter(item => Boolean(item.document.selectedForDeletion));
    const indexes = itemsToDelete.map(item => localCheckList.indexOf(item));

    const newCheckList = collection.removeRange(indexes, localCheckList);
    const newCheckListValidated = validateCollection(newCheckList);
    setLocalCheckList(newCheckListValidated);

    setItemsToDelete(0);
    setListDirty(true);

    if (localFilteredCheckList && localFilteredCheckList.length - 1 === 0) {
      onChangePage(paginationAndFilterObj.page - 1, newCheckListValidated.length, newCheckListValidated);
      return;
    }

    setLocalFilteredCheckList(_prevState =>
      getItemsPerPage(newCheckListValidated, paginationAndFilterObj.pageSize, paginationAndFilterObj.page, paginationAndFilterObj.filter)
    );
  };

  const toggleItemDeletion = idx => {
    const deletionStatus = Boolean(localCheckList[idx]?.document.selectedForDeletion);

    if (!deletionStatus) setItemsToDelete(itemsToDelete + 1);
    else setItemsToDelete(itemsToDelete - 1);

    setLocalCheckList(prevState =>
      collection.update(
        idx + paginationAndFilterObj.pageSize * (paginationAndFilterObj.page - 1),
        editableDocument.update('selectedForDeletion', !deletionStatus, null),
        prevState
      )
    );

    setLocalFilteredCheckList(prevState =>
      collection.update(idx, editableDocument.update('selectedForDeletion', !deletionStatus, null), prevState)
    );
  };

  const save = async _idx => {
    const checkListValidationList = validateCollection(localCheckList);

    if (!checkListValidationList.every(x => x.validationInfo.isValid)) {
      const firstFailedIndex = checkListValidationList.indexOf(checkListValidationList.find(x => !x.validationInfo.isValid));

      if (firstFailedIndex !== -1) {
        let indexPage = 0;

        for (let i = 0; i <= Math.ceil(checkListValidationList.length / paginationAndFilterObj.pageSize); i++) {
          if (
            !(i * paginationAndFilterObj.pageSize <= firstFailedIndex && firstFailedIndex <= (i + 1) * paginationAndFilterObj.pageSize - 1)
          )
            indexPage++;
          else break;
        }
        onChangePage(indexPage, null, checkListValidationList);
      }

      return;
    }

    const filteredChecklistValidationList = validateCollection(localFilteredCheckList);

    setLocalCheckList(checkListValidationList);
    setLocalFilteredCheckList(filteredChecklistValidationList);

    setSaving(true);

    const listToSave = localCheckList.map(item => item.document);
    await dispatch(
      isFromFolder
        ? updateCheckList(offerData.offerId, offerData.offerSiteId, documentId, siteId, listToSave)
        : updateCheckList(documentId, siteId, folderData.folderId, folderData.folderSiteId, listToSave)
    );

    setSaving(false);
    setListDirty(false);
  };

  const validateCollection = collection => {
    return collection.map(editableDocument.validate(getValidationRules(collection), false));
  };

  const getItemsPerPage = (checklist, pageSize, page, filter, newCount = null) => {
    let current = (page - 1) * pageSize;
    const itemsOnPage = checklist.slice(current, current + pageSize);
    let result;

    if (filter !== '') {
      result = filterByValue(checklist, filter);
      setPaginationAndFilterObj({
        ...paginationAndFilterObj,
        totalCount: result.length,
        page: defaultConfig.page,
        filter: filter
      });
    } else {
      result = itemsOnPage;
      setPaginationAndFilterObj({
        ...paginationAndFilterObj,
        totalCount: newCount ?? checklist.length,
        page: page,
        filter: filter
      });
    }
    return result;
  };

  const filterByValue = (checklist, filter) => {
    const result = checklist.filter(o =>
      Object.keys(o.document).some(k => {
        if (o.document[k] !== null && k === 'condition')
          return o.document[k].toString().toLowerCase().includes(filter.toString().toLowerCase());
        return null;
      })
    );
    return result;
  };

  const updatePageSize = (items, paginationAndFilterObj) => {
    const page = paginationAndFilterObj.page;
    const pageSize = paginationAndFilterObj.pageSize;
    const filter = paginationAndFilterObj.filter;
    setLocalFilteredCheckList(getItemsPerPage(items, pageSize, page, filter));
  };

  const onChangePage = (page, newCount = null, newList = null) => {
    const newPage = ++page;
    updatePageSize(newList ?? localCheckList, {
      ...paginationAndFilterObj,
      page: newPage,
      ...(newCount && { totalCount: newCount })
    });
    setPaginationAndFilterObj({
      ...paginationAndFilterObj,
      page: newPage,
      ...((newCount || newCount === 0) && { totalCount: newCount })
    });
  };

  const onChangeRowsPerPage = size => {
    if (localCheckList.length < size) {
      updatePageSize(localCheckList, { ...paginationAndFilterObj, pageSize: size, page: defaultConfig.page });
      setPaginationAndFilterObj({ ...paginationAndFilterObj, pageSize: size, page: defaultConfig.page });
    } else {
      updatePageSize(localCheckList, { ...paginationAndFilterObj, pageSize: size });
      setPaginationAndFilterObj({ ...paginationAndFilterObj, pageSize: size });
    }
  };

  const onPaginationAndFiltersRefresh = () => {
    updatePageSize(localCheckList, {
      ...paginationAndFilterObj,
      pageSize: defaultConfig.pageSize,
      filter: defaultConfig.filter
    });
    setPaginationAndFilterObj({ ...defaultConfig, totalCount: checkList.length });
  };

  const resetFilters = () => {
    filterChangedDebounced('');
  };

  const onFilterValueChangeDebounce = filter => {
    updatePageSize(localCheckList, { ...defaultConfig, filter: filter });
  };

  const filterChangedDebounced = debounce(onFilterValueChangeDebounce, DEBOUNCE_DELAY);

  const onFilterValueChanged = (value, debounced) => {
    if (debounced) filterChangedDebounced(value);
  };

  return (
    <CheckListComponent
      filteredCheckList={localFilteredCheckList}
      checkList={localCheckList}
      onSave={save}
      onAdd={addItem}
      onDelete={deleteItems}
      toggleItemDeletion={toggleItemDeletion}
      onPropertyChange={changeItemProperty}
      checklists={checklists}
      userId={userId}
      saving={saving}
      isDirty={isListDirty}
      isValid={getItemsValidity(localCheckList)}
      getItemsDirtyStatus={getItemsDirtyStatus}
      checkListLoaded={checkListLoaded}
      documentChecklistWrite={documentChecklistWrite}
      readOnlyFull={readOnlyFull}
      onFilterValueChanged={onFilterValueChanged}
      rowsPerPageOptions={rowsPerPageOptions}
      onChangeRowsPerPage={onChangeRowsPerPage}
      onChangePage={onChangePage}
      onRefresh={onPaginationAndFiltersRefresh}
      paginationAndFilterObj={paginationAndFilterObj}
      fullTextFilter={paginationAndFilterObj.filter}
      resetFilters={resetFilters}
      itemsToDelete={itemsToDelete}
      addDeleteApprovalConditions={addDeleteApprovalConditions}
    />
  );
};

CheckListContainer.propTypes = {
  documentId: PropTypes.number,
  siteId: PropTypes.number,
  checkList: PropTypes.array,
  checkListLoaded: PropTypes.bool.isRequired,
  userId: PropTypes.number,
  userName: PropTypes.string,
  isFromFolder: PropTypes.bool,
  checklistsLoaded: PropTypes.bool,
  checklists: PropTypes.array.isRequired
};

const mapStateToProps = (state, route) => {
  const key = `${route.documentId}-${route.siteId}`;
  const { checklistDetails, loaded } = selectCheckList(state, key);

  return {
    checkList: checklistDetails,
    checkListLoaded: loaded,
    userId: selectUserId(state),
    userName: selectCurrentUserName(state),
    checklistsLoaded: selectChecklistsLoaded(state),
    checklists: selectChecklists(state)
  };
};

export default compose(connect(mapStateToProps))(CheckListContainer);
